import React from "react";
import { format } from "date-fns";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateRangeFilter } from "@/components/ui/date-range-filter";
import { FilterMenu } from "@/components/ui/filter-menu";
import { ADVICE_TYPE_OPTIONS } from "@/types/treatment-overview";
import type { TreatmentOverviewFilters as FilterState } from "@/types/treatment-overview";
import type { DateRange } from "react-day-picker";

interface FilterOption {
  label: string;
  value: string;
}

interface TreatmentOverviewFiltersProps {
  filters: FilterState;
  providerOptions: FilterOption[];
  onProviderTypeChange: (value: string) => void;
  onAdviceTypeChange: (value: string) => void;
  onDateRangeChange: (startDate: string | null, endDate: string | null) => void;
  onClearFilters: () => void;
  isUsingDefaultDateRange?: boolean;
  defaultDateRange?: {
    startDate: string | null;
    endDate: string | null;
  };
  useDarkOverlays?: boolean;
  showDateRange?: boolean;
}

const FILTER_TRIGGER =
  "border-border/70 bg-background px-3 shadow-none hover:bg-accent/20";
const FILTER_CONTENT = "border-border/70 bg-popover shadow-md";

export const TreatmentOverviewFilters = ({
  filters,
  providerOptions,
  onProviderTypeChange,
  onAdviceTypeChange,
  onDateRangeChange,
  onClearFilters,
  isUsingDefaultDateRange = false,
  defaultDateRange,
  useDarkOverlays = false,
  showDateRange = true,
}: TreatmentOverviewFiltersProps) => {
  const dateRange: DateRange | undefined =
    filters.startDate && filters.endDate
      ? { from: new Date(filters.startDate), to: new Date(filters.endDate) }
      : undefined;

  const overlayThemeClass = useDarkOverlays ? "dark" : undefined;
  const selectedProviders = filters.providerType ?? [];
  const selectedAdvice = filters.adviceType ?? [];

  const hasActiveFilters =
    selectedProviders.length > 0 ||
    selectedAdvice.length > 0 ||
    (showDateRange && !isUsingDefaultDateRange && dateRange);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Provider — multi-select, derived from table data */}
      {providerOptions.length > 0 && (
        <FilterMenu
          label="Provider"
          options={providerOptions}
          value={selectedProviders}
          onValueChange={onProviderTypeChange}
          multiple
          triggerClassName={FILTER_TRIGGER}
          contentClassName={
            overlayThemeClass
              ? `${FILTER_CONTENT} ${overlayThemeClass}`
              : FILTER_CONTENT
          }
        />
      )}

      {/* Advice — multi-select */}
      <FilterMenu
        label="Advice"
        options={[...ADVICE_TYPE_OPTIONS]}
        value={selectedAdvice}
        onValueChange={onAdviceTypeChange}
        multiple
        triggerClassName={FILTER_TRIGGER}
        contentClassName={
          overlayThemeClass
            ? `${FILTER_CONTENT} ${overlayThemeClass}`
            : FILTER_CONTENT
        }
      />

      {/* Date Range */}
      {showDateRange && (
        <DateRangeFilter
          value={dateRange}
          onChange={(range) =>
            onDateRangeChange(
              range?.from
                ? format(range.from, "yyyy-MM-dd")
                : (defaultDateRange?.startDate ?? null),
              range?.to
                ? format(range.to, "yyyy-MM-dd")
                : (defaultDateRange?.endDate ?? null)
            )
          }
          placeholder="Date range"
          triggerClassName={`${FILTER_TRIGGER} font-normal`}
          contentClassName={
            overlayThemeClass
              ? `${FILTER_CONTENT} ${overlayThemeClass}`
              : FILTER_CONTENT
          }
          badgeLabel={isUsingDefaultDateRange ? "Default" : undefined}
          resetSelectionOnOpen={isUsingDefaultDateRange}
          disabled={{ after: new Date() }}
        />
      )}

      {/* Clear all — icon button to match other toolbars */}
      {hasActiveFilters ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onClearFilters}
          className="h-9 w-9 rounded-xl border-border/70 bg-background/80 text-muted-foreground shadow-none hover:text-foreground"
          aria-label="Clear filters"
          title="Clear filters"
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
};
